import { useEffect, useRef, useState } from 'react'
import { HiOutlineCheck, HiOutlineChevronDown } from 'react-icons/hi'

function UniversalSelector({ icon, title, items, selectedId, onSelect, selectionDescriptor = null, align = 'right' })
{
    const [open, setOpen] = useState(false)
    const containerRef = useRef(null)
    const closeTimer = useRef(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!containerRef.current) {
                return
            }
            const target = event.target
            const isNode = typeof Node !== 'undefined' && target instanceof Node
            if (isNode && !containerRef.current.contains(target)) {
                setOpen(false)
            }
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
            document.removeEventListener('keydown', handleKeyDown)
            if (closeTimer.current !== null) {
                clearTimeout(closeTimer.current)
            }
        }
    }, [])

    const clearCloseTimer = () => {
        if (closeTimer.current !== null) {
            clearTimeout(closeTimer.current)
            closeTimer.current = null
        }
    }

    const scheduleClose = () => {
        clearCloseTimer()
        closeTimer.current = setTimeout(() => setOpen(false), 120)
    }

    const selectItem = (id) => {
        setOpen(false)
        if (id !== selectedId) {
            onSelect(id)
        }
    }

    return (
        <div
            className={`selector ${open ? 'open' : ''}`}
            ref={containerRef}
            onPointerEnter={(event) => {
                if (event.pointerType === 'touch') {
                    return
                }
                clearCloseTimer()
                setOpen(true)
            }}
            onPointerLeave={(event) => {
                if (event.pointerType === 'touch') {
                    return
                }
                scheduleClose()
            }}
        >
            <button
                type="button"
                className="selector-trigger"
                onClick={() => setOpen((current) => !current)}
                aria-label={title}
                title={title}
                aria-expanded={open}
            >
                <span className="selector-icon">{icon}</span>
                <HiOutlineChevronDown className="selector-caret" aria-hidden="true" />
            </button>
            <div className={`selector-menu ${align === 'left' ? 'align-left' : 'align-right'}`} role="menu">
                {(selectionDescriptor !== null) ? <span className="description">{selectionDescriptor}</span> : null}
                {items.map((item) => (
                    <button
                        type="button"
                        className={`selector-item ${item.id === selectedId ? 'selected' : ''}`}
                        key={item.id}
                        onClick={() => selectItem(item.id)}
                        role="menuitem"
                    >
                        <div className="selector-item-text">
                            <span className="selector-item-label">{item.label}</span>
                            {item.description ? (
                                <span className="selector-item-description">{item.description}</span>
                            ) : null}
                        </div>
                        {item.id === selectedId ? <HiOutlineCheck className="selector-check" aria-hidden="true" /> : null}
                    </button>
                ))}
            </div>
        </div>
    )
}

export default UniversalSelector
